'use client';

/**
 * useScrollEdgeFade — feathers the edges of a bounded scrollport (sticky rail,
 * max-h + overflow-y-auto) instead of letting content hard-slice at the clip line.
 *
 * Contract (proven on the market terminal's right rail, 2026-06-12):
 *   - an edge fades ONLY when content actually continues past it — at scroll-top
 *     there is no top fade, at scroll-end no bottom fade, and when the content
 *     fits there is no mask at all (crisp edges, zero visual cost);
 *   - driven by scroll + ResizeObserver (container AND its first child), so async
 *     content swaps (docked panels, loaded feeds) re-evaluate automatically.
 *
 * Usage:
 *   const rail = useScrollEdgeFade<HTMLDivElement>([deps that swap the content]);
 *   <div ref={rail.ref} onScroll={rail.onScroll} className="… overflow-y-auto" />
 */

import { useCallback, useEffect, useRef } from 'react';

const FADE = '20px';

export function useScrollEdgeFade<T extends HTMLElement>(deps: unknown[] = []) {
  const ref = useRef<T>(null);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const top = max > 2 && el.scrollTop > 2;
    const bottom = max > 2 && el.scrollTop < max - 2;
    const mask =
      top && bottom
        ? `linear-gradient(to bottom, transparent, black ${FADE}, black calc(100% - ${FADE}), transparent)`
        : top
          ? `linear-gradient(to bottom, transparent, black ${FADE})`
          : bottom
            ? `linear-gradient(to bottom, black calc(100% - ${FADE}), transparent)`
            : '';
    el.style.setProperty('mask-image', mask);
    el.style.setProperty('-webkit-mask-image', mask);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    onScroll();
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ref, onScroll };
}
